import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCRM } from '../contexts/CRMContext';

const { FiBarChart3, FiTrendingUp, FiDollarSign, FiUsers } = FiIcons;

const Reports = () => {
  const { contacts, leads, deals, tasks } = useCRM();

  const totalDealValue = deals.reduce((sum, deal) => sum + (deal.value || 0), 0);
  const closedDeals = deals.filter(deal => deal.stage === 'closed_won');
  const closedDealValue = closedDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);

  const stats = [
    {
      title: 'Total Contacts',
      value: contacts.length,
      icon: FiUsers,
      color: 'bg-blue-500'
    },
    {
      title: 'Active Leads',
      value: leads.filter(lead => lead.status !== 'lost' && lead.status !== 'converted').length,
      icon: FiTrendingUp,
      color: 'bg-green-500'
    },
    {
      title: 'Pipeline Value',
      value: `$${totalDealValue.toLocaleString()}`,
      icon: FiDollarSign,
      color: 'bg-purple-500'
    },
    {
      title: 'Closed Deals',
      value: `$${closedDealValue.toLocaleString()}`,
      icon: FiBarChart3,
      color: 'bg-orange-500'
    }
  ];

  const leadsBySource = leads.reduce((acc, lead) => {
    acc[lead.source] = (acc[lead.source] || 0) + 1;
    return acc;
  }, {});

  const dealsByStage = deals.reduce((acc, deal) => {
    acc[deal.stage] = (acc[deal.stage] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600">Track your business performance and insights</p>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center">
              <div className={`${stat.color} rounded-lg p-3`}>
                <SafeIcon icon={stat.icon} className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Source */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Leads by Source</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {Object.entries(leadsBySource).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 capitalize">{source}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(count / leads.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Deals by Stage */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Deals by Stage</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {Object.entries(dealsByStage).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {stage.replace('_', ' ')}
                  </span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${(count / deals.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-lg shadow"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Performance Summary</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {((closedDeals.length / deals.length) * 100 || 0).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Deal Close Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                ${(closedDealValue / closedDeals.length || 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mt-1">Average Deal Size</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {tasks.filter(task => task.completed).length}
              </div>
              <div className="text-sm text-gray-600 mt-1">Completed Tasks</div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Reports;